import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { Button, ModalContentContainer } from '@/components';
import { AnalyticsService, EVENTS } from '@/services';
import {
  selectActiveModalParams,
  uiActions,
  useAppDispatch,
  useAppSelector,
  useArchiveOrganizationMembersMutation,
} from '@/store';
import { OrganizationMember } from '@/types';
import { SnackbarUtils } from '@/utils';

export const MemberArchiveModalContent = () => {
  const dispatch = useAppDispatch();

  const [archiveOrganizationMember, { isLoading: isArchiveMembersLoading }] =
    useArchiveOrganizationMembersMutation();

  const { organizationMembers, organizationMemberIds } = (useAppSelector(
    selectActiveModalParams,
  ) || {}) as {
    organizationMembers?: OrganizationMember[];
    organizationMemberIds?: number[];
  };
  const memberIds =
    organizationMemberIds ||
    organizationMembers?.map(member => member.organizationMemberId) ||
    [];

  const memberCount = memberIds?.length;

  const onClose = () => dispatch(uiActions.closeActiveModal());
  const onArchive = async () => {
    try {
      AnalyticsService.logEvent(EVENTS.MEMBER_MANAGEMENT_EVENTS.REMOVE.INTENT);
      await archiveOrganizationMember(memberIds).unwrap();

      AnalyticsService.logEvent(EVENTS.MEMBER_MANAGEMENT_EVENTS.REMOVE.SUCCESS);
      SnackbarUtils.success(
        `${memberCount} member${memberCount > 1 ? 's' : ''} removed successfully`,
      );
      onClose();
    } catch {
      AnalyticsService.logEvent(EVENTS.MEMBER_MANAGEMENT_EVENTS.REMOVE.FAIL);
      SnackbarUtils.error('Failed to remove member(s), please try again later');
    }
  };

  return (
    <ModalContentContainer onModalClose={onClose} stretch>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h2">
          Remove{memberCount > 1 ? ` ${memberCount}` : ''} Member
          {memberCount > 1 && 's'}?
        </Typography>
        <Typography variant="body2">
          Are you sure you want to remove{' '}
          {memberCount > 1 ? 'these members' : 'this member'} from your LumiQ
          plan?
        </Typography>
        <Typography variant="body2">
          They will still be able to access their professional development
          certificates, but won&apos;t be able to earn new ones.
        </Typography>
        {organizationMembers && organizationMembers?.length > 0 && (
          <TableContainer sx={styles.tableContainer}>
            <Table>
              <TableHead>
                <TableRow sx={styles.tableRow}>
                  <TableCell sx={styles.tableCell}>
                    <Typography variant="h5">EMAIL</Typography>
                  </TableCell>
                  <TableCell sx={styles.tableCell}>
                    <Typography variant="h5">NAME</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {organizationMembers?.map((member: OrganizationMember) => (
                  <TableRow
                    key={member.organizationMemberId}
                    sx={styles.tableRow}>
                    <TableCell sx={styles.tableCell}>
                      <Typography variant="body2">
                        {member.organizationEmail}
                      </Typography>
                    </TableCell>
                    <TableCell sx={styles.tableCell}>
                      <Typography variant="body2">{member.name}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="secondary"
            skinny={false}
            label="Cancel"
            onClick={onClose}
          />
          <Button
            variant="primary"
            skinny={false}
            label="Remove"
            disabled={isArchiveMembersLoading}
            onClick={onArchive}
          />
        </Box>
      </Box>
    </ModalContentContainer>
  );
};

const styles = {
  tableContainer: {
    mb: 3,
    maxHeight: 320,
    overflowY: 'auto',
    msOverflowStyle: 'initial',
    scrollbarWidth: 'initial',
    '&::-webkit-scrollbar': {
      display: 'block',
      width: 5,
      height: 8,
      backgroundColor: 'grey50',
      borderRadius: 5,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'grey100',
      borderRadius: 5,
    },
  },
  tableRow: {
    '&:last-child td': {
      border: 0,
    },
  },
  tableCell: {
    pl: 0,
  },
};
